import Styles from './styles.module.css';
import React, { useState, useEffect } from 'react';
import DefaultTitle from '../../../../package/title/index';
import StoreImage from '../StoreImage';
import { doc, getFirestore, updateDoc } from 'firebase/firestore';
import { getStorage, ref, uploadBytes } from 'firebase/storage';

type StoreEditProps = {
  editInfo: editInfoList;
  setEditInfo: React.Dispatch<React.SetStateAction<editInfoList>>;
};

interface editInfoList {
  id: string,
  name: string,
  number: number,
  lastTime: number,
}

const StoreEdit: React.FC<StoreEditProps> = ({ editInfo, setEditInfo }) => {

  const isWindows = navigator.userAgent.includes("Windows");
  const modalZoom = isWindows ? 1 / 0.8 : 1;

  const cropImageToCoverBlob = (
    file: File,
    targetWidth: number,
    targetHeight: number,
  ): Promise<Blob> => {
    return new Promise((resolve, reject) => {

      const img = new Image();
      const reader = new FileReader();

      reader.onload = () => {
        if (typeof reader.result !== "string") return reject("Invalid file");
  
        img.src = reader.result;
        img.crossOrigin = "anonymous";
  
        img.onload = () => {

          const imgAspect = img.width / img.height;
          const targetAspect = targetWidth / targetHeight;
  
          let sx = 0, sy = 0, sWidth = img.width, sHeight = img.height;
  
          if (imgAspect > targetAspect) {
            sWidth = img.height * targetAspect;
            sx = (img.width - sWidth) / 2;
          } else {
            sHeight = img.width / targetAspect;
            sy = (img.height - sHeight) / 2;
          }
  
          const canvas = document.createElement("canvas");
          canvas.width = targetWidth;
          canvas.height = targetHeight;
  
          const ctx = canvas.getContext("2d");
          if (!ctx) return reject("No canvas context");
  
          ctx.drawImage(img, sx, sy, sWidth, sHeight, 0, 0, targetWidth, targetHeight);
  
          canvas.toBlob((blob) => {
            if (!blob) return reject("Canvas toBlob failed");
            resolve(blob);
          }, "image/png");
        };
  
        img.onerror = reject;
      };

      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const [name, setName] = useState("");
  const [errorName, setErrorName] = useState(false);

  const [styleImageFile, setStyleImageFile] = useState("");
  const [styleImageFilePath, setStyleImageFilePath] = useState<File | null>(null);

  const [uploading, setUploading] = useState(false);

  const isLocked = uploading;

  const db = getFirestore();
  const storage = getStorage();

  const [ time, setTime ] = useState<number>(0);

  // ======================
  // 初期セット
  // ======================
  useEffect(() => {
    var timeInit = new Date().getTime();
    setTime(timeInit);

    setName(editInfo.name);
  }, [editInfo]);

  // ======================
  // 画像選択
  // ======================
  const styleImages = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (isLocked) return;

    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];

      setStyleImageFile(URL.createObjectURL(file));
      setStyleImageFilePath(file);
    }
  };

  // ======================
  // 保存処理
  // ======================
  async function save() {
    if (isLocked) return;

    if (!name.trim()) {
      setErrorName(true);
      return;
    }

    setErrorName(false);
    setUploading(true);

    try {

      // 画像アップロード
      if (styleImageFilePath) {
        const storageRef = ref(storage, `/stores/${editInfo.id}`);
        await uploadBytes(storageRef, styleImageFilePath);

        if (editInfo.number === 1) {
          try {
            const jobpPostingsUpload = ref(storage, `/jobPostingsStore/${editInfo.id}`);
            const fileToUpload = await cropImageToCoverBlob(styleImageFilePath, 1200, 800);

            await uploadBytes(jobpPostingsUpload, fileToUpload);
          } catch (error) {
            console.error("画像アップロードまたはクロップに失敗しました", error);
          }
        }
      }

      // Firestore更新
      await updateDoc(doc(db, "stores", editInfo.id), {
        name,
        last_time: new Date(),
      });

      // 閉じる
      setEditInfo({ id: "", name: "", number: 0, lastTime: 0 });
    } catch (err) {
      console.error(err);
    } finally {
      setUploading(false);
    }
  }

  return (
    <>
    <div className={Styles.box} style={{ zoom: modalZoom }}>

      {/* ================= overlay ================= */}

      <div>
        <div
          className={Styles.box_black}
          onClick={() => {
            if (isLocked) return;
            setEditInfo({ id: "", name: "", number: 0, lastTime: 0 });
          }}
        />

        <div className={Styles.box_contents} style={isWindows ? { transform: 'scale(0.8)' } : {}}>

          {/* ================= header ================= */}
          <div className={Styles.title}>
            <h4>店舗編集</h4>

            <button onClick={save} disabled={isLocked}>
              {isLocked ? "保存中..." : "更新"}
            </button>
          </div>


          <p className={Styles.desc}>
            ※店舗情報を編集してください
          </p>
          
          <DefaultTitle title={'店舗写真'} />

          {/* ================= image（10.salonPr/staff/make と同じ画像input） ================= */}
          <label className={Styles.imageBox}>
            {styleImageFile ? (
              <img className={Styles.imagePreview} src={styleImageFile} alt="店舗写真" />
            ) : (
              <StoreImage
                image={editInfo.id}
                cacheKey={editInfo.lastTime}
                alt="店舗写真"
                className={Styles.imagePreview}
                placeholderClassName={Styles.imageLoading}
                loadingText
              />
            )}
            <input
              type="file"
              accept="image/*"
              onChange={styleImages}
              disabled={isLocked}
              className={Styles.hiddenInput}
            />
          </label>

          {/* ================= name ================= */}
          <div className={Styles.title}>
            <div>
              <p>店舗名</p>
              <div className={Styles.title_yes}>※必須</div>
            </div>
            <p>{name.length} / 20</p>
          </div>

          <input
            className={Styles.input}
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={isLocked}
          />

          <p className={Styles.desc} style={errorName ? { color: 'var(--mw-error)', fontWeight: 'bold' } : undefined}>※店舗名を入力してください。</p>

        </div>
      </div>
    </div>

    {isLocked && (
      <div className={Styles.lockOverlay}>
        <div className={Styles.spinner} />
        <p>アップロード中...</p>
      </div>
    )}
    </>
  );
}

export default StoreEdit;